<script module lang="ts">
	// The link test (UC-58, #775; decision 2026-10-22-onboarding-tests-the-link):
	// Test the link to one other till of the shop, and its answer in place
	// under it, a refusal saying why in a flag Band. Sharing with this
	// shop's tills (T28) shows one per till; the cash-point step (T32, #792)
	// reuses it for the chosen cash point, which takes each answer through
	// onAnswer. An answer that changes the list it sits in (sharing off, or
	// the till gone) goes to the list's owner when it takes it, which says it
	// where it stays.
	import type { MessageKey } from '../i18n/messages';
	import type { LinkAnswer } from '../till/mesh.svelte';

	/** An answer's words and how it shows: a status line, or a flag Band. */
	export function linkTestLine(answer: LinkAnswer): { key: MessageKey; tone: 'status' | 'flag' } {
		const keys: Record<LinkAnswer, MessageKey> = {
			answered: 'linkAnswered',
			not_heard: 'linkNotHeard',
			no_answer: 'linkNoAnswer',
			mesh_off: 'linkMeshOff',
			gone: 'linkGone',
			failed: 'printerFailed',
		};
		return { key: keys[answer], tone: answer === 'answered' ? 'status' : 'flag' };
	}

	/** The action's word: once a test answered, it tests again. */
	export function linkTestLabel(answered: boolean): MessageKey {
		return answered ? 'linkTestAgain' : 'linkTest';
	}
</script>

<script lang="ts">
	import { onDestroy } from 'svelte';
	import Button from '../components/Button.svelte';
	import Band from '../components/Band.svelte';
	import { fill, message, type Language } from '../i18n/messages';
	import { testLink, MESH_SLOW_MS } from '../till/mesh.svelte';

	interface Props {
		language: Language;
		tillId: string;
		name: string;
		disabled?: boolean;
		/** A test starts. */
		onStart?: () => void;
		/** Sharing is off on this till now; the owner reads the mesh again. */
		onMeshOff?: (name: string) => void;
		/** The till is no longer one of the shop's; the owner reads the list again. */
		onGone?: (name: string) => void;
		/** Each test's answer, once it came. */
		onAnswer?: (answer: LinkAnswer) => void;
	}
	let {
		language,
		tillId,
		name,
		disabled = false,
		onStart,
		onMeshOff,
		onGone,
		onAnswer,
	}: Props = $props();
	let testing = $state(false);
	let slow = $state(false);
	let answered = $state(false);
	let answer = $state<LinkAnswer | null>(null);
	let timer: ReturnType<typeof setTimeout> | undefined;
	let disposed = false;
	onDestroy(() => {
		disposed = true;
		clearTimeout(timer);
	});
	const line = $derived(answer === null ? null : linkTestLine(answer));
	const delegated = $derived(
		(answer === 'mesh_off' && !!onMeshOff) || (answer === 'gone' && !!onGone),
	);

	async function test() {
		if (testing || disabled) return;
		testing = true;
		answer = null;
		onStart?.();
		timer = setTimeout(() => (slow = true), MESH_SLOW_MS);
		const result = await testLink(tillId);
		clearTimeout(timer);
		if (disposed) return;
		testing = false;
		slow = false;
		answered = true;
		answer = result;
		onAnswer?.(result);
		if (result === 'mesh_off') onMeshOff?.(name);
		if (result === 'gone') onGone?.(name);
	}
</script>

<div class="link-test">
	<Button
		testId="link-test-{tillId}"
		tone="secondary"
		size="sm"
		label={message(language, linkTestLabel(answered))}
		state={testing && slow ? 'pending' : 'idle'}
		pendingLabel={message(language, 'linkTesting')}
		{disabled}
		onPress={() => void test()}
	/>
	{#if line && !delegated}
		{#if line.tone === 'status'}
			<p role="status" data-testid="link-test-answer-{tillId}">
				{fill(message(language, line.key), name)}
			</p>
		{:else}
			<Band tone="flag" alert testId="link-test-refusal-{tillId}"
				>{fill(message(language, line.key), name)}</Band
			>
		{/if}
	{/if}
</div>

<style>
	.link-test {
		display: flex;
		flex-direction: column;
		align-items: start;
		gap: var(--space-3);
	}
	p {
		margin: 0;
	}
</style>
